import React, { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
    makeLinkVideoURL,
    makeVideoDownloadUrl,
} from '../../services/discovery/_shared/urlMakers';
import { fetchCustomSourceSet } from '../../services/discovery/fetchCustomSourceSet';

import { usePrivileges } from '../../state/auth/index.hooks';
import { setVideoTime, toggleVideoDialog } from '../../state/ui/videoUrl';
import {
    useVideoDialog,
    useVideoParams,
} from '../../state/ui/videoUrl/index.hooks';

import TranslationHelper from '../../helpers/TranslationHelper';
import { errorMessageHandler } from '../../helpers/errorMessageHandler';

import { useAppDispatch, useToolkitDispatch } from '../../hooks';

import {
    CAMERAS_RANGE,
    LABELS_MODEL_NOT_SELECTED,
} from '../../constants/videoPlayerSettings';
import { TOOLTIP_DEBOUNCE } from '../../constants/DebounceValues';

import VideoPlayerDialog, {
    ALL_CAMERAS_ID,
} from '../dialogs/VideoPlayerDialog/VideoPlayerDialog';
import DialogTitle from '../dialogs/DialogTitle/DialogTitle';
import PlayerDropdownOptions from '../PlayerDropdownOptions';

import SelectField from '../SelectField';
import {
    buildFetchParams,
    constructVideoParams,
    getAvailableCamerasIds,
    parseQueryParams,
} from './helpers';
import { downloadFrame } from '../../helpers/moviePlayerOptions/downloadFrame';
import downloadMovie from '../../helpers/moviePlayerOptions/downloadMovie';

interface IVideoUrlParams {
    sliderTimeRange: number[];
    paramName: string;
    anonymize: boolean;
    eventId: string;
    hls: boolean;
    labels: boolean;
    model?: string;
    confidenceThreshold?: number;
}

const BLANK_PARAMS = {
    sliderTimeRange: CAMERAS_RANGE,
    paramName: '',
    anonymize: false,
    eventId: '',
    hls: false,
    labels: false,
    model: LABELS_MODEL_NOT_SELECTED,
    confidenceThreshold: 0.85,
};
const LinkVideoPlayer = () => {
    const [copyVideoLinkTooltip, setCopyVideoLinkTooltip] = useState(
        TranslationHelper.translate('Copy only video link to clipboard')
    );

    const [ids, setIds] = useState<string[]>([]);

    const [open, setOpen] = useState(false);
    const [videoParams, setVideoParams] =
        useState<IVideoUrlParams>(BLANK_PARAMS);
    const privileges = usePrivileges();
    const dialog = useVideoDialog();
    const queryParams = useVideoParams();

    const { enqueueSnackbar } = useSnackbar();

    const videoRef = useRef<HTMLVideoElement | null>(null);

    const dispatch = useAppDispatch();

    const toolkitDispatch = useToolkitDispatch();
    const [downloadInProgress, setDownloadInProgress] = useState(false);

    useEffect(() => {
        if (!queryParams) return;

        const parsed = parseQueryParams(queryParams);

        fetchCustomSourceSet(
            buildFetchParams(parsed.timestamp, parsed.monitoredId)
        )
            .then((data) => {
                setVideoParams(
                    constructVideoParams(
                        data,
                        parsed.selectedCamera,
                        parsed.range,
                        privileges,
                        parsed.anonymize,
                        parsed.labels,
                        parsed.model,
                        parsed.confidenceThreshold
                    )
                );
                const availableIds = getAvailableCamerasIds(data).sort();
                setIds(
                    availableIds.length > 1
                        ? [...availableIds, ALL_CAMERAS_ID]
                        : availableIds
                );
            })
            .catch((e) => {
                enqueueSnackbar(errorMessageHandler(e.status)(), {
                    variant: 'error',
                });
            });
    }, [queryParams, dispatch, privileges, enqueueSnackbar]);

    useEffect(() => {
        if (videoParams.eventId) {
            setOpen(true);
        }
    }, [videoParams]);

    if (!queryParams) {
        return null;
    }

    const changeCamera = (cameraName: string) => {
        const videoUrlParams = {
            ...videoParams,
            paramName: cameraName,
        };
        setVideoParams(videoUrlParams);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const copyVideoLink = () => {
        setCopyVideoLinkTooltip(
            TranslationHelper.translate('Link was created and copied!')
        );
        setTimeout(() =>
            copyToClipboard(
                makeLinkVideoURL({
                    ...queryParams,
                    selectedCamera: videoParams.paramName,
                }).toString()
            )
        );
        setTimeout(
            () =>
                setCopyVideoLinkTooltip(
                    TranslationHelper.translate(
                        'Copy only video link to clipboard'
                    )
                ),
            TOOLTIP_DEBOUNCE
        );
    };
    const handleChangeCamera = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        toolkitDispatch(setVideoTime(videoRef.current?.currentTime));
        setOpen(false);
        changeCamera(event.target.value);
    };

    const handleClose = (event?: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        toolkitDispatch(toggleVideoDialog());
    };

    const renderDropdownButtons = () => {
        return (
            <PlayerDropdownOptions
                copyVideoLink={{
                    title: copyVideoLinkTooltip,
                    callback: copyVideoLink,
                }}
                downloadProps={downloadProps}
            />
        );
    };
    const showNotification = (success: boolean, message: string) => {
        enqueueSnackbar(message, {
            variant: success ? 'success' : 'error',
        });
    };

    const handleDownloadFrame = () => {
        if (videoParams && queryParams) {
            downloadFrame({
                videoRef,
                quality: 1,
                range: videoParams.sliderTimeRange,
                date: queryParams.timestamp,
                selectedCamera: videoParams.paramName,
            });
        }
    };
    const handleDownloadMovie = async () => {
        if (!videoParams) {
            return;
        }
        setDownloadInProgress(true);
        downloadMovie({
            url: makeVideoDownloadUrl({
                ...videoParams,
                hls: false,
            }).toString(),
            onSuccess: () => {
                setDownloadInProgress(false);
            },
            onError: () => {
                showNotification(
                    false,
                    TranslationHelper.translate(
                        'An error has occurred, please try again later'
                    )
                );
            },
        });
    };

    const isAllCamerasSelected = () => {
        return (videoParams?.paramName ?? ALL_CAMERAS_ID) === ALL_CAMERAS_ID;
    };

    const downloadProps = isAllCamerasSelected()
        ? undefined
        : {
              handleDownloadFrame,
              handleDownloadMovie,
              downloadInProgress,
          };

    const renderTitleButton = () => {
        return (
            <SelectField
                name="selectedCamera"
                value={videoParams.paramName}
                ids={ids}
                handleChange={handleChangeCamera}
                translateContext={'mp'}
            />
        );
    };
    const getSrcUrl = (paramName: string) => {
        const updatedParams = { ...videoParams, paramName };
        return makeVideoDownloadUrl(updatedParams).toString();
    };
    return (
        <>
            {open && (
                <VideoPlayerDialog
                    getSrcUrl={getSrcUrl}
                    videoRef={videoRef}
                    open={dialog}
                    handleClose={handleClose}
                    type={
                        videoParams.hls ? 'application/x-mpegURL' : 'video/mp4'
                    }
                    selectedCamera={videoParams.paramName}
                    ids={ids}
                >
                    <DialogTitle
                        title={`XTrack Player:`}
                        close={handleClose}
                        dropDown={renderDropdownButtons}
                        titleButton={renderTitleButton}
                    />
                </VideoPlayerDialog>
            )}
        </>
    );
};

export default LinkVideoPlayer;
